import { Navigation } from "@/components/Navigation"
import { Footer } from "@/components/Footer"
import { ArrowRight } from "lucide-react"
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion"
import { Helmet } from "react-helmet-async"

import { SEO } from "@/components/SEO"
import { LeorixAISchema } from "@/components/schema"

const leorixFAQs = [
    {
        question: "Does LeoRix offer more than platforms like Salesforce or HubSpot?",
        answer: "Yes. While Salesforce and HubSpot are powerful systems of record, LeoRix is a 'System of Intelligence.' It unifies these disconnected tools into a single cognitive layer, allowing you to trigger complex, cross-platform workflows (e.g., update CRM + notify Slack + generate contract) with natural language, going far beyond standard automation.",
    },
    {
        question: "Is my data secure?",
        answer: "Yes. We use a 'Privacy-First' architecture where data processing happens within your secure environment. We are SOC2 Type II compliant and offer on-premise deployment options for enterprise clients.",
    },
    {
        question: "Can I customize the 'Hey Leo' commands?",
        answer: "Absolutely. Commands are fully customizable. You can define specific workflows, triggers, and complex multi-step actions that align with your unique business processes.",
    },
    {
        question: "Is Leorix a revenue-generating ERP Application?",
        answer: "Yes. Leorix acts as a comprehensive, revenue-centric ERP solution that spans the entire value chain - from Lead Generation to Revenue. By integrating Human Resources, Finance, Asset Management, and Operations into a unified cognitive fabric, it eliminates data silos and optimizes every interaction for growth.",
    },
    {
        question: "How can RSA Tech Group help us implement LeoRix?",
        answer: "LeoRix is part of the RSA Tech Group Cognitive Ecosystem, ensuring a seamless implementation. RSA Tech Group handles the entire deployment lifecycle. We map your data silos, configure the AI transformation rules to match your business logic, and build custom 'Skills' for Leo to ensure immediate ROI from day one.",
    },
]

const generateFAQSchema = () => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: leorixFAQs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
            "@type": "Answer",
            text: faq.answer,
        },
    })),
})

const modules = [
    {
        name: "Business Leads",
        tag: "LEADS",
        description: "99.9% accurate and verified business leads with access to millions of decision-makers across industries.",
        features: ["Verified Contacts", "Industry Filters", "Decision-Maker Access", "Bulk Export"],
    },
    {
        name: "CRM",
        tag: "CRM",
        description: "Comprehensive customer relationship management with pipeline tracking and AI-powered sales insights.",
        features: ["Pipeline Tracking", "AI Insights", "Deal Scoring", "Auto Follow-up"],
    },
    {
        name: "HR Management",
        tag: "HR",
        description: "Complete human resources management - recruitment, onboarding, performance reviews, and employee engagement.",
        features: ["Recruitment", "Onboarding", "Performance", "Engagement"],
    },
    {
        name: "FinTech",
        tag: "FINANCE",
        description: "Modern financial technology and payment solutions - invoicing, expense tracking, and financial visibility.",
        features: ["Invoicing", "Expense Tracking", "Payments", "Financial Reports"],
    },
    {
        name: "Contracts",
        tag: "LEGAL",
        description: "AI-powered contract creation, negotiation, signing, and lifecycle management in one platform.",
        features: ["AI Drafting", "E-Signatures", "Negotiation", "Compliance"],
    },
]

export default function LeorixAI() {
    const faqSchema = generateFAQSchema()
    return (
        <div className="min-h-screen bg-white">
            <SEO
                title="LeoRix AI | The All-in-One AI Enterprise Hub"
                description="One Command. Instant Transformation. Build, automate, and connect your entire enterprise ecosystem with LeoRix - the AI-powered enterprise hub."
                image="https://leorix.com/sharing.png"
            />
            <Helmet>
                <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
            </Helmet>
            <LeorixAISchema />
            <Navigation />

            {/* Hero - Dark */}
            <div className="relative pt-48 pb-24 overflow-hidden bg-[#0f0a1f]">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_30%,rgba(124,58,237,0.2),transparent_60%)]" />
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#7C3AED]/10 rounded-full blur-[150px]" />
                <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#A78BFA]/[0.06] rounded-full blur-[120px]" />

                <div className="section-container relative z-10 text-center">
                    <div className="max-w-5xl mx-auto">
                        {/* Logo-style title */}
                        <h1 className="text-6xl md:text-8xl lg:text-9xl font-black text-white mb-4 leading-[0.9] tracking-tighter">
                            Leo<span className="text-transparent bg-clip-text bg-gradient-to-r from-[#A78BFA] to-[#7C3AED]">Rix</span>
                        </h1>
                        <p className="text-lg md:text-xl text-[#A78BFA] font-semibold tracking-wide uppercase mb-8">
                            Your All-in-One AI Enterprise Hub
                        </p>

                        <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto mb-4 font-light leading-relaxed">
                            One command. Instant transformation. Build, automate, and connect your entire enterprise ecosystem with just your voice.
                        </p>
                        <p className="text-base text-slate-400 max-w-2xl mx-auto mb-12 font-light italic">
                            "Hey Leo, generate a quarterly report from CRM data and email it to the leadership team."
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <a
                                href="https://app.leorix.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-white bg-[#7C3AED] rounded-full hover:bg-[#6D28D9] transition-all duration-300 shadow-[0_8px_30px_rgba(124,58,237,0.3)] hover:shadow-[0_12px_40px_rgba(124,58,237,0.4)] hover:-translate-y-0.5"
                            >
                                Start Free Trial <ArrowRight className="ml-2 h-5 w-5" />
                            </a>
                            <a
                                href="https://leorix.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-white bg-white/[0.06] border border-white/[0.12] rounded-full hover:bg-white/[0.10] hover:border-[#7C3AED]/40 transition-all duration-300"
                            >
                                Visit leorix.com
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            {/* Stats Bar - Light */}
            <section className="py-16 bg-gradient-to-b from-slate-50 via-white to-white relative overflow-hidden">
                <div
                    className="absolute inset-0 opacity-[0.15]"
                    style={{
                        backgroundImage: "radial-gradient(circle, #7C3AED 0.5px, transparent 0.5px)",
                        backgroundSize: "28px 28px",
                    }}
                />
                <div className="section-container relative z-10">
                    <div className="grid md:grid-cols-4 gap-8 max-w-5xl mx-auto">
                        {[
                            { value: "40%", label: "Productivity Increase", sub: "average across customers" },
                            { value: "30%", label: "Cost Reduction", sub: "in operational expenses" },
                            { value: "50%", label: "Faster Time-to-Market", sub: "for new initiatives" },
                            { value: "4.8", label: "Customer Rating", sub: "out of 5 stars" },
                        ].map((stat, i) => (
                            <div key={i} className="text-center group">
                                <div className="text-4xl md:text-5xl font-black text-[#7C3AED] mb-2 tracking-tight group-hover:scale-105 transition-transform duration-300">
                                    {stat.value}
                                </div>
                                <div className="text-[#2e1065] text-sm font-bold mb-1">{stat.label}</div>
                                <div className="text-slate-400 text-xs font-light">{stat.sub}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* What is LeoRix - Dark */}
            <section className="py-24 relative overflow-hidden bg-[#0f0a1f]">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_50%,rgba(124,58,237,0.12),transparent_70%)]" />
                <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-[#A78BFA]/[0.04] rounded-full blur-[120px]" />

                <div className="section-container relative z-10">
                    <div className="grid lg:grid-cols-2 gap-20 items-center max-w-6xl mx-auto">
                        <div>
                            <p className="text-[#A78BFA] text-sm font-bold uppercase tracking-[0.2em] mb-4">
                                The Transformation Layer
                            </p>
                            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white tracking-tight leading-[1.1] mb-8">
                                One Platform.{" "}
                                <span className="text-[#A78BFA]">Every Function.</span>{" "}
                                Zero Silos.
                            </h2>
                            <p className="text-slate-400 text-lg font-light leading-relaxed mb-6">
                                LeoRix is the cognitive layer that unifies your entire enterprise - from Lead Generation to Revenue. It centralizes Leads, CRM, HR, Finance, and Contracts into one AI-powered hub, eliminating the fragmentation that costs enterprises over $2.5M annually.
                            </p>
                            <p className="text-slate-500 text-base font-light leading-relaxed mb-10">
                                With the Leo Marketing Campaign Suite, you can track ROI and get AI-powered insights across 12+ channels - all triggered with a single voice command.
                            </p>

                            <div className="space-y-6">
                                {[
                                    { metric: "10x", title: "Productivity Boost", desc: "Automate repetitive processes and trigger complex cross-platform workflows instantly." },
                                    { metric: "40%", title: "Cost Reduction", desc: "Consolidate disconnected tools and reclaim lost revenue from poor data integration." },
                                    { metric: "24/7", title: "AI Operations", desc: "Always-on automation with 99.9% verified business insights and real-time intelligence." },
                                ].map((item, i) => (
                                    <div key={i} className="flex gap-5 group">
                                        <div className="w-14 h-14 bg-[#7C3AED]/10 border border-[#7C3AED]/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                                            <span className="text-sm font-black text-[#A78BFA]">{item.metric}</span>
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-black text-white tracking-tight mb-1">{item.title}</h3>
                                            <p className="text-slate-400 text-sm font-light leading-relaxed">{item.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Voice Command Demo */}
                        <div className="relative">
                            <div className="absolute inset-0 bg-[#7C3AED]/[0.04] rounded-3xl blur-[60px]" />
                            <div className="relative bg-white/[0.04] backdrop-blur-sm border border-white/[0.08] rounded-3xl p-8 md:p-10">
                                {/* Terminal Header */}
                                <div className="flex items-center gap-2 mb-6 pb-4 border-b border-white/[0.06]">
                                    <div className="flex gap-1.5">
                                        <div className="w-3 h-3 rounded-full bg-red-500/60" />
                                        <div className="w-3 h-3 rounded-full bg-yellow-500/60" />
                                        <div className="w-3 h-3 rounded-full bg-green-500/60" />
                                    </div>
                                    <span className="text-[10px] text-slate-500 uppercase tracking-[0.2em] font-bold ml-3">LeoRix AI Terminal</span>
                                </div>

                                {/* Command */}
                                <div className="space-y-5">
                                    <div>
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className="text-[#A78BFA] text-xs font-black">YOU</span>
                                            <span className="text-slate-600 text-[10px]">just now</span>
                                        </div>
                                        <div className="bg-[#7C3AED]/[0.10] border border-[#7C3AED]/20 rounded-xl p-4">
                                            <p className="text-white text-sm font-medium">"Hey Leo, pull Q4 leads, update CRM pipeline, and draft contracts for the top 10 deals."</p>
                                        </div>
                                    </div>

                                    <div>
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className="text-[#A78BFA] text-xs font-black">LEO</span>
                                            <span className="relative flex h-2 w-2">
                                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#A78BFA] opacity-40" />
                                                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#7C3AED]" />
                                            </span>
                                        </div>
                                        <div className="bg-white/[0.04] border border-white/[0.08] rounded-xl p-4 space-y-3">
                                            {[
                                                { text: "Pulled 847 Q4 leads from Business Leads module" },
                                                { text: "Updated CRM pipeline - 23 deals moved to Stage 3" },
                                                { text: "Drafted 10 contracts - awaiting your review" },
                                            ].map((step, i) => (
                                                <div key={i} className="flex items-start gap-3">
                                                    <div className="w-5 h-5 bg-[#7C3AED]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                                        <span className="text-[#A78BFA] text-[8px] font-black">&#10003;</span>
                                                    </div>
                                                    <p className="text-slate-300 text-sm font-light">{step.text}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-6 pt-4 border-t border-white/[0.06] flex items-center justify-between">
                                    <span className="text-[10px] text-slate-500 uppercase tracking-wider font-bold">3 workflows executed</span>
                                    <span className="text-[10px] text-[#A78BFA] font-bold">1.2s total</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Enterprise Modules - Light */}
            <section className="py-24 bg-gradient-to-b from-slate-50 via-white to-slate-50 relative overflow-hidden">
                <div
                    className="absolute inset-0 opacity-[0.2]"
                    style={{
                        backgroundImage: "radial-gradient(circle, #7C3AED 0.5px, transparent 0.5px)",
                        backgroundSize: "28px 28px",
                    }}
                />
                <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-[#7C3AED]/[0.02] rounded-full blur-[150px]" />
                <div className="absolute bottom-0 right-1/3 w-[400px] h-[400px] bg-[#A78BFA]/[0.02] rounded-full blur-[120px]" />

                <div className="section-container relative z-10">
                    <div className="text-center mb-20 max-w-3xl mx-auto">
                        <p className="text-[#7C3AED] text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                            Enterprise Modules
                        </p>
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#2e1065] tracking-tight leading-[1.1] mb-6">
                            Five Modules.{" "}
                            <span className="text-[#7C3AED]">One Brain.</span>
                        </h2>
                        <p className="text-slate-500 text-lg font-light leading-relaxed">
                            Every module is deeply integrated with AI - not bolted on. LeoRix doesn't just store data, it understands, connects, and acts on it.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                        {modules.map((mod, i) => (
                            <div
                                key={i}
                                className={`partner-card group rounded-3xl overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_25px_80px_rgba(124,58,237,0.12)] transition-all duration-500 hover:-translate-y-2 ${i === 0 ? "lg:col-span-2 lg:row-span-1" : ""}`}
                            >
                                <div className="absolute inset-0 z-20 overflow-hidden rounded-3xl pointer-events-none">
                                    <div className="absolute inset-0 w-1/3 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-[shine_0.8s_ease-in-out]" />
                                </div>
                                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#7C3AED] to-[#A78BFA] scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left z-10" />
                                <div className="absolute -top-16 -right-16 w-44 h-44 bg-[#7C3AED]/[0.04] rounded-full blur-[50px] group-hover:bg-[#7C3AED]/[0.10] group-hover:scale-125 transition-all duration-700" />
                                <span className="absolute top-6 right-8 text-[120px] font-black text-[#7C3AED]/[0.03] leading-none select-none group-hover:text-[#7C3AED]/[0.06] transition-colors duration-500">
                                    {String(i + 1).padStart(2, "0")}
                                </span>

                                <div className="p-8 md:p-10 h-full flex flex-col relative z-10">
                                    <div className="mb-5">
                                        <span className="px-3 py-1 bg-[#7C3AED]/[0.06] border border-[#7C3AED]/[0.12] rounded-md text-[10px] font-black text-[#7C3AED] uppercase tracking-wider">
                                            {mod.tag}
                                        </span>
                                    </div>

                                    <h3 className="text-2xl font-black text-[#2e1065] tracking-tight mb-3 group-hover:text-[#7C3AED] transition-colors duration-300">
                                        {mod.name}
                                    </h3>
                                    <p className="text-slate-500 text-sm leading-relaxed font-light mb-6">
                                        {mod.description}
                                    </p>

                                    <div className="flex flex-wrap gap-2 mt-auto pt-6 border-t border-slate-100">
                                        {mod.features.map((feat, fi) => (
                                            <span
                                                key={fi}
                                                className="px-3 py-1.5 bg-[#7C3AED]/[0.04] text-[#7C3AED]/70 text-xs font-medium rounded-full border border-[#7C3AED]/[0.08] hover:bg-[#7C3AED]/10 hover:border-[#7C3AED]/30 hover:text-[#7C3AED] transition-all duration-300 cursor-default"
                                            >
                                                {feat}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* How It Works - Dark */}
            <section className="py-24 relative overflow-hidden bg-[#0f0a1f]">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_50%,rgba(124,58,237,0.12),transparent_70%)]" />
                <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-[#A78BFA]/[0.04] rounded-full blur-[120px]" />

                <div className="section-container relative z-10">
                    <div className="text-center mb-20 max-w-3xl mx-auto">
                        <p className="text-[#A78BFA] text-sm font-bold uppercase tracking-[0.2em] mb-4">
                            How It Works
                        </p>
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white tracking-tight leading-[1.1]">
                            From Voice Command{" "}
                            <span className="text-[#A78BFA]">to Enterprise Action</span>
                        </h2>
                    </div>

                    <div className="grid md:grid-cols-4 gap-6 max-w-5xl mx-auto relative">
                        {/* Connecting line */}
                        <div className="hidden md:block absolute top-14 left-[15%] right-[15%] h-[2px] bg-gradient-to-r from-[#7C3AED]/30 via-white/[0.06] to-[#7C3AED]/30 z-0" />

                        {[
                            { num: "01", title: "Speak", desc: "Issue a natural language command to Leo" },
                            { num: "02", title: "Understand", desc: "AI parses intent, context, and required modules" },
                            { num: "03", title: "Execute", desc: "Cross-module workflows trigger automatically" },
                            { num: "04", title: "Deliver", desc: "Results surface in real-time with full audit trail" },
                        ].map((step) => (
                            <div key={step.num} className="relative z-10 text-center group">
                                <div className="w-24 h-24 mx-auto bg-[#0f0a1f] border-2 border-[#7C3AED]/25 rounded-full flex items-center justify-center mb-6 shadow-[0_0_30px_rgba(124,58,237,0.08)] group-hover:border-[#7C3AED]/50 group-hover:shadow-[0_0_40px_rgba(124,58,237,0.18)] transition-all duration-500">
                                    <span className="text-2xl font-black text-[#A78BFA]">{step.num}</span>
                                </div>
                                <h3 className="text-lg font-black text-white tracking-tight mb-2">
                                    {step.title}
                                </h3>
                                <p className="text-slate-500 text-sm font-light leading-relaxed max-w-[180px] mx-auto">
                                    {step.desc}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Security & Compliance - Light */}
            <section className="py-24 bg-gradient-to-b from-slate-50 via-white to-slate-50 relative overflow-hidden">
                <div
                    className="absolute inset-0 opacity-[0.15]"
                    style={{
                        backgroundImage: "radial-gradient(circle, #7C3AED 0.5px, transparent 0.5px)",
                        backgroundSize: "28px 28px",
                    }}
                />
                <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-[#7C3AED]/[0.02] rounded-full blur-[150px]" />

                <div className="section-container relative z-10">
                    <div className="max-w-5xl mx-auto">
                        <div className="text-center mb-16">
                            <p className="text-[#7C3AED] text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                                Enterprise Grade
                            </p>
                            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#2e1065] tracking-tight leading-[1.1] mb-6">
                                Privacy-First{" "}
                                <span className="text-[#7C3AED]">Architecture</span>
                            </h2>
                            <p className="text-slate-500 text-lg font-light leading-relaxed max-w-3xl mx-auto">
                                Data processing happens within your secure environment. LeoRix is SOC2 Type II compliant with on-premise deployment options for enterprise clients who need full sovereignty.
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                            <div className="partner-card group rounded-3xl overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_25px_80px_rgba(124,58,237,0.12)] transition-all duration-500 hover:-translate-y-2">
                                <div className="absolute inset-0 z-20 overflow-hidden rounded-3xl pointer-events-none">
                                    <div className="absolute inset-0 w-1/3 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-[shine_0.8s_ease-in-out]" />
                                </div>
                                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#7C3AED] to-[#A78BFA] scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left z-10" />
                                <div className="p-8 md:p-10 relative z-10">
                                    <div className="text-xs font-bold text-[#7C3AED] uppercase tracking-[0.2em] mb-4">Security</div>
                                    <h3 className="text-2xl font-black text-[#2e1065] tracking-tight mb-3">Data Sovereignty</h3>
                                    <p className="text-slate-500 text-sm font-light leading-relaxed mb-6">
                                        Your data stays within your secure environment. Full control over processing, storage, and access - zero compromises.
                                    </p>
                                    <div className="flex flex-wrap gap-2 pt-5 border-t border-slate-100">
                                        {["SOC2 Type II", "On-Premise", "Encryption", "RBAC"].map((tag, i) => (
                                            <span key={i} className="px-3 py-1.5 bg-[#7C3AED]/[0.04] text-[#7C3AED]/70 text-xs font-medium rounded-full border border-[#7C3AED]/[0.08] hover:bg-[#7C3AED]/10 hover:border-[#7C3AED]/30 hover:text-[#7C3AED] transition-all duration-300 cursor-default">
                                                {tag}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="partner-card group rounded-3xl overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_25px_80px_rgba(124,58,237,0.12)] transition-all duration-500 hover:-translate-y-2">
                                <div className="absolute inset-0 z-20 overflow-hidden rounded-3xl pointer-events-none">
                                    <div className="absolute inset-0 w-1/3 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-[shine_0.8s_ease-in-out]" />
                                </div>
                                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#7C3AED] to-[#A78BFA] scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left z-10" />
                                <div className="p-8 md:p-10 relative z-10">
                                    <div className="text-xs font-bold text-[#7C3AED] uppercase tracking-[0.2em] mb-4">Compliance</div>
                                    <h3 className="text-2xl font-black text-[#2e1065] tracking-tight mb-3">Enterprise Compliance</h3>
                                    <p className="text-slate-500 text-sm font-light leading-relaxed mb-6">
                                        Built-in compliance frameworks with full audit trails, role-based access, and industry-standard certifications.
                                    </p>
                                    <div className="flex flex-wrap gap-2 pt-5 border-t border-slate-100">
                                        {["Audit Logs", "GDPR Ready", "HIPAA", "ISO 27001"].map((tag, i) => (
                                            <span key={i} className="px-3 py-1.5 bg-[#7C3AED]/[0.04] text-[#7C3AED]/70 text-xs font-medium rounded-full border border-[#7C3AED]/[0.08] hover:bg-[#7C3AED]/10 hover:border-[#7C3AED]/30 hover:text-[#7C3AED] transition-all duration-300 cursor-default">
                                                {tag}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto mt-10">
                            {[
                                { value: "0", label: "Security Incidents", sub: "since launch" },
                                { value: "100%", label: "Data Sovereignty", sub: "your data, your rules" },
                                { value: "256-bit", label: "Encryption", sub: "at rest & in transit" },
                                { value: "99.9%", label: "Uptime SLA", sub: "guaranteed availability" },
                            ].map((item, i) => (
                                <div key={i} className="text-center group py-6">
                                    <div className="text-2xl font-black text-[#7C3AED] mb-1 group-hover:scale-105 transition-transform">{item.value}</div>
                                    <div className="text-[#2e1065] text-xs font-bold mb-0.5">{item.label}</div>
                                    <div className="text-slate-400 text-[10px] font-light">{item.sub}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ - Dark */}
            <section className="py-24 relative overflow-hidden bg-[#0f0a1f]">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_50%,rgba(124,58,237,0.08),transparent_70%)]" />

                <div className="section-container max-w-3xl relative z-10">
                    <div className="text-center mb-16">
                        <p className="text-[#A78BFA] text-sm font-bold uppercase tracking-[0.2em] mb-4">
                            FAQ
                        </p>
                        <h2 className="text-3xl md:text-4xl font-black text-white tracking-tight leading-[1.1]">
                            Frequently Asked{" "}
                            <span className="text-[#A78BFA]">Questions</span>
                        </h2>
                        <p className="text-slate-500 text-base mt-4 font-light">
                            Common questions about transforming your enterprise with LeoRix.
                        </p>
                    </div>
                    <Accordion type="single" collapsible className="w-full space-y-3">
                        {leorixFAQs.map((faq, i) => (
                            <AccordionItem
                                key={i}
                                value={`item-${i}`}
                                className="bg-white/[0.03] border border-white/[0.08] rounded-xl px-6 hover:border-[#7C3AED]/25 transition-colors duration-300 data-[state=open]:border-[#7C3AED]/40"
                            >
                                <AccordionTrigger className="text-base font-bold text-white text-left hover:no-underline hover:text-[#A78BFA] transition-colors py-5">
                                    {faq.question}
                                </AccordionTrigger>
                                <AccordionContent className="text-slate-400 text-sm font-light leading-relaxed pb-5">
                                    {faq.answer}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </section>

            {/* CTA - Dark */}
            <section className="py-28 relative overflow-hidden bg-[#0f0a1f]">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_50%,rgba(124,58,237,0.15),transparent_70%)]" />
                <div className="absolute bottom-0 left-1/4 w-[500px] h-[500px] bg-[#A78BFA]/[0.04] rounded-full blur-[150px]" />

                <div className="section-container relative z-10 text-center">
                    <p className="text-[#A78BFA] text-sm font-bold uppercase tracking-[0.2em] mb-6">
                        Get Started
                    </p>
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white tracking-tight leading-[1.1] mb-6">
                        Experience the Power of{" "}
                        <span className="text-[#A78BFA]">AI-Driven Enterprise</span>
                    </h2>
                    <p className="text-slate-400 text-lg mb-10 max-w-2xl mx-auto font-light leading-relaxed">
                        Start your free trial today. No credit card required. One command is all it takes.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <a
                            href="https://app.leorix.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-white bg-[#7C3AED] rounded-full hover:bg-[#6D28D9] transition-all duration-300 shadow-[0_8px_30px_rgba(124,58,237,0.3)] hover:shadow-[0_12px_40px_rgba(124,58,237,0.4)] hover:-translate-y-0.5"
                        >
                            Start Free Trial <ArrowRight className="ml-2 h-5 w-5" />
                        </a>
                        <a
                            href="https://leorix.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-white bg-white/[0.06] border border-white/[0.12] rounded-full hover:bg-white/[0.10] hover:border-[#7C3AED]/40 transition-all duration-300"
                        >
                            Learn More at leorix.com
                        </a>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    )
}
